/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-23
 */

import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import type { MessageResponseSummary } from '../../../types/classEssentials';

interface MessageCentricViewProps {
  data: MessageResponseSummary[];
  loading?: boolean;
}

/**
 * Message-centric view of Class Essentials responses
 * Shows aggregated data grouped by checklist message
 */
export const MessageCentricView: React.FC<MessageCentricViewProps> = ({
  data,
  loading = false,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No sent messages found. Send a checklist message to start collecting
          responses.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {data.map(message => (
        <Accordion key={message.message_id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ width: '100%', pr: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="h6">{message.message_title}</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${message.responded_recipients}/${message.total_recipients} Responded`}
                    color={message.response_rate >= 80 ? 'success' : 'warning'}
                    size="small"
                  />
                  <Chip
                    label={`${message.response_rate}%`}
                    color={message.response_rate >= 80 ? 'success' : 'warning'}
                    size="small"
                  />
                </Stack>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={message.response_rate}
                  color={message.response_rate >= 80 ? 'success' : 'warning'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Status Breakdown */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Response Breakdown
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    label={`Already Have: ${message.status_breakdown.already_have}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Want to Purchase: ${message.status_breakdown.want_to_purchase}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Purchase Online: ${message.status_breakdown.purchase_online}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Purchase from Studio: ${message.status_breakdown.purchase_in_person}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Stack>
              </Box>

              {/* Recipients List */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Recipients ({message.recipients.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Parent</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Responses</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {message.recipients.map(recipient => (
                        <TableRow key={recipient.email}>
                          <TableCell>
                            {recipient.has_responded ? (
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                              />
                            ) : (
                              <RadioButtonUncheckedIcon
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>{recipient.parent_name || '—'}</TableCell>
                          <TableCell>{recipient.student_name || '—'}</TableCell>
                          <TableCell>{recipient.email}</TableCell>
                          <TableCell align="right">
                            {recipient.response_count}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Inventory Planning */}
              {message.inventory_items.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Studio Purchase Inventory ({message.inventory_items.length}{' '}
                    items)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Revenue Estimate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {message.inventory_items.map(item => (
                          <TableRow key={item.item_name}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.item_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.item_description || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {item.item_price !== null
                                ? `$${Number(item.item_price).toFixed(2)}`
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {item.quantity_needed}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                              >
                                {item.revenue_estimate !== null
                                  ? `$${Number(item.revenue_estimate).toFixed(2)}`
                                  : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};
